import { cloneDeep } from 'lodash-es';
import { IStorage } from '../../core/storage/Contract';
import {WardDto} from "server-openapi";
import itiriri from "itiriri";
import { DateUtils } from "../../core/utils/dateUtils";

export interface ChangeNumberWithDateString {
    changeNumber: number;
    syncToDate: string; // ISO string
}

export interface ChangeNumberWithDate {
    changeNumber: number;
    syncToDate?: Date | undefined;
}

export function createChangeNumberWithDate(
    changeNumber: number,
    date: Date = new Date()
): ChangeNumberWithDateString {
    return {
        changeNumber,
        syncToDate: DateUtils.fromDate(date),
    };
}

interface HsObject {
  hsId?: number;
  clinicalSystemId?: string | null;
  changeNumber?: number | null;
}
interface SyncstreamV2Object {
  hsId?: number;
  changeNumber?: number | null;
}

export const SyncUtils = {
  async getChangeNumberForFacilities(
    facilityIds: number[] | string[],
    changeNumberStore: IStorage<number | undefined>,
  ): Promise<number> {
    const facilityChangeNumberLookups = facilityIds.map(async (facId) => {
      return (await changeNumberStore.get(facId!.toString())) ?? 0;
    });
    const facilityChangeNumbers = await Promise.all(facilityChangeNumberLookups);
    if (!facilityChangeNumbers || facilityChangeNumbers.length === 0) {
      return 0;
    }
    return Math.min(...facilityChangeNumbers);
  },

    async getChangeNumberWithDateForFacilities(
        facilityIds: number[] | string[],
        changeNumberWithDateStore: IStorage<ChangeNumberWithDateString>,
    ): Promise<ChangeNumberWithDate> {
        const values = await Promise.all(
            facilityIds.map((facId) => changeNumberWithDateStore.get(facId.toString()))
        );

        // If list is empty or any facility is missing, return default: changeNumber 0 and no date
        if (values.some((v) => v === undefined)) {
            return { changeNumber: 0, syncToDate: undefined };
        }

        // All present. Normally and (by contract) all facilityids for a group get stored in there with identical change number values,
        // however, a facility could get moved in from a different group and therefore potentially have a different change number which
        // may be smaller. So we need to get the smallest change number and date across all items.
        const allChangeNumsWithDates = values as ChangeNumberWithDateString[];

        // Smallest changeNumber across all items (doesn't need to match the date's item)
        const num = allChangeNumsWithDates.reduce((min, v) => Math.min(min, v.changeNumber ?? 0), Infinity);
        const minChangeNumber = Number.isFinite(num) ? num : 0;

        // Smallest syncToDate across all items (ISO strings compare lexicographically)
        const minSyncToDateIso = allChangeNumsWithDates.reduce<string | undefined>((minIso, v) => {
            const iso = v.syncToDate;
            if (!iso) return minIso;
            if (!minIso) return iso;
            return iso < minIso ? iso : minIso;
        }, undefined);

        const date = minSyncToDateIso ? new Date(minSyncToDateIso) : undefined;
        return { changeNumber: minChangeNumber, syncToDate: date };

    },

  async setChangeNumberForFacilities(
    facilityIds: string[] | number[],
    changeNumberStore: IStorage<number | undefined>,
    data: HsObject[],
  ) {
    if (!data || data.length === 0) {
      return;
    }
    const changeNumber = data[data.length - 1].changeNumber;
    if (!changeNumber) {
      return;
    }

    return await changeNumberStore.setMany(
      facilityIds.map((id) => ({
        key: id.toString(),
        value: changeNumber
      })),
    );
  },
    async setChangeNumberDirectlyForFacilities(
        facilityIds: string[] | number[],
        changeNumberStore: IStorage<number | undefined>,
        changeNumber: number
    ) {
        return await changeNumberStore.setMany(
            facilityIds.map((id) => ({
                key: id.toString(),
                value: changeNumber
            })),
        );
    },

  async deleteChangeNumberForFacilities(
    facilityIds: string[] | number[],
    changeNumberStore: IStorage<number | undefined>,
  ) {
    if (!facilityIds || facilityIds.length === 0) {
        return;
    }

    return await changeNumberStore.deleteMany(
     facilityIds.map((id) => id.toString())
    );
  },

  async deleteAllChangeNumberForFacilities(
    changeNumberStore: IStorage<number | undefined>,
  ) {
    return await changeNumberStore.clear();
  },

    async setChangeNumberWithDateForFacilities(
      facilityIds: string[] | number[],
      changeNumberWithDateStore: IStorage<ChangeNumberWithDateString>,
      syncToDate: Date,
      changeNumber: number
  ) {
      return await changeNumberWithDateStore.setMany(
          facilityIds.map((id) => ({
              key: id.toString(),
              value: createChangeNumberWithDate(changeNumber, syncToDate)
          })),
      );
   },

  async setChangeNumberForFacilityGroup(
      facilityGroupId: string,
      changeNumberStore: IStorage<number | undefined>,
      data: SyncstreamV2Object[],
  ) {
    if (!data || data.length === 0) {
      return;
    }
    const changeNumber = data[data.length - 1].changeNumber;
    if (!changeNumber) {
      return;
    }
    return await changeNumberStore.set(facilityGroupId, changeNumber);
  },

  getLatestChangeNumberForV2Data<T extends SyncstreamV2Object>(data: T[]) {
    if (data.length == 0) {
      return 0;
    }
    return data[data.length - 1].changeNumber ?? 0;
  },
  getLatestChangeNumber<T extends HsObject>(data: T[]) {
      if (data.length == 0) {
        return 0;
      }
      return data[data.length - 1].changeNumber ?? 0;
  },
  async getFacilitiesForGroup(
      facilityGroupId: string | undefined,
      facilitiesStore: IStorage<WardDto>) {
    if (!facilityGroupId) {
      return [];
    }
    const facilities = await facilitiesStore.all();

    return itiriri(facilities.values())
        .filter((facility) => facility.facilityGroupId?.toString() === facilityGroupId)
        .toArray();
  },


  OverrideClinicalSystemId<T extends HsObject>(data: T[]) {
    const cloned = this.deepClone<T>(data);
    cloned.forEach((item) => {
      /* eslint-disable eqeqeq */
      if (item.clinicalSystemId == null) {
        item.clinicalSystemId = item.hsId?.toString();
      }
    });
    return cloned;
  },

  deepClone<T>(data: T[]) {
    const cloned: T[] = [];
    data.forEach((item) => cloned.push(cloneDeep(item)));
    return cloned;
  },
};
